import { useState } from "react";
import { Film, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { BUSES, renderMix, encodeWav } from "@/lib/audioEngine";
import { toCube } from "@/lib/grade";
import { uploadFile, waitReady } from "@/lib/upload";
import { api, formatApiError, formatTime } from "@/lib/api";

const Row = ({ label, hint, checked, onChange, testId }) => (
  <div className="flex items-start justify-between gap-4 py-2">
    <div><p className="text-sm">{label}</p><p className="text-xs text-slate-500">{hint}</p></div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

export const ExportDialog = ({ open, onOpenChange, projectId, projectName, grade, clips, mixer, engine, onCreated }) => {
  const [name, setName] = useState(projectName);
  const [burn, setBurn] = useState(true);
  const [withMix, setWithMix] = useState(true);
  const [loud, setLoud] = useState(-14);
  const [stage, setStage] = useState("");
  const vids = clips.filter((c) => c.track === "video");
  const fxClips = clips.filter((c) => c.track === "fx");
  const aud = clips.filter((c) => ["audio", "sfx", "score"].includes(c.track));
  const duration = Math.max(0, ...clips.map((c) => c.start + c.len));

  const start = async () => {
    if (!vids.length) return toast.error("Add footage to the picture track first");
    try {
      let mixId = null;
      if (withMix && aud.length) {
        setStage("Bouncing music, SFX & score…");
        const buffers = new Map();
        for (const c of aud) buffers.set(c.asset_id, await engine.load(c.asset_id));
        const blob = encodeWav(await renderMix(aud, buffers, mixer, duration));
        const up = await uploadFile({ projectId, file: blob, name: `${name}_mix.wav`, type: "audio/wav", kind: "mixdown",
          onProgress: (_, p) => setStage(`Uploading mix ${p}%`) });
        setStage("Finalising mix…");
        mixId = (await waitReady(up.id)).id;
      }
      setStage("Starting render…");
      const anySolo = BUSES.some((b) => mixer.buses[b.id].solo);
      const f = mixer.buses.footage;
      const { data } = await api.post("/export/video", {
        project_id: projectId, name, duration, mix_asset_id: mixId, master: mixer.master, loudness: loud,
        cube: burn ? toCube(grade, name, 33) : null,
        clips: vids.map((c) => ({ asset_id: c.asset_id, in_point: c.in, out_point: c.out, trans_type: c.transDur > 0 ? c.trans.type : "none", trans_dur: c.transDur || 0, ...(c.aud && { aud: c.aud }) })),
        fx: fxClips.map((c) => ({ asset_id: c.asset_id, in_point: c.in, out_point: c.out, start: c.start, ...c.fx })),
        footage: { ...f, mute: f.mute || (anySolo && !f.solo) },
      });
      onCreated(data);
      toast.success("Render started — track progress in the Exports bin");
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setStage(""); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !stage && onOpenChange(o)}>
      <DialogContent className="bg-[#13151C] border-[#2A2E3D]" data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Export finished video</DialogTitle>
          <DialogDescription>H.264 MP4 · up to 1080p · 30 fps · AAC 192 kbps</DialogDescription>
        </DialogHeader>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="File name" className="bg-[#0B0C10]" data-testid="export-name-input" />
        <div className="divide-y divide-[#2A2E3D]">
          <Row label="Burn in colour grade" hint="Applies your current look (same maths as the .cube LUT)" checked={burn} onChange={setBurn} testId="export-burn-grade-switch" />
          <Row label="Include music, SFX & score" hint={`${aud.length} audio clip(s), mixed through the console`} checked={withMix} onChange={setWithMix} testId="export-include-mix-switch" />
        </div>
        <div data-testid="export-loudness">
          <p className="text-sm">Loudness target</p>
          <p className="text-xs text-slate-500 mb-2">Normalises the finished mix so it plays at a consistent volume</p>
          <div className="grid grid-cols-4 gap-1">
            {[[null, "Off", "as mixed"], [-14, "Online", "-14 LUFS"], [-16, "Streaming", "-16 LUFS"], [-23, "Broadcast", "-23 LUFS"]].map(([v, l, h]) => (
              <button key={l} onClick={() => setLoud(v)} data-testid={`export-loudness-${v == null ? "off" : -v}`}
                className={`rounded-md py-1.5 text-[11px] border transition-colors ${loud === v ? "bg-[#FFB800] border-[#FFB800] text-[#0B0C10] font-semibold" : "bg-[#0B0C10] border-[#2A2E3D] text-slate-300 hover:border-[#3F455A]"}`}>
                {l}<span className="block text-[9px] opacity-70">{h}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="font-mono-ui text-[11px] text-slate-400 bg-[#0B0C10] rounded-md p-3" data-testid="export-summary">
          {vids.length} picture clip(s) · {fxClips.length} FX layer(s) · length {formatTime(duration)} · picture audio via Dialogue bus
        </div>
        <Button onClick={start} disabled={!!stage || !vids.length} className="w-full bg-[#FFB800] text-[#0B0C10] hover:bg-[#ffc933]" data-testid="export-start-button">
          {stage ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{stage}</> : <><Film className="w-4 h-4 mr-2" />Render & export</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
